/**
 * TextArea Component
 *
 * A reusable textarea component with consistent styling and theme support.
 * Provides a clean interface for multi-line text input with proper focus states
 * and accessibility features.
 *
 * @module components/ui/TextArea
 */

import React, { forwardRef } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { cn } from '@/utils/cn';

/**
 * Props interface for TextArea component
 */
export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Additional CSS classes */
  className?: string;
  /** Number of visible text lines */
  rows?: number;
  /** Whether the textarea can be resized */
  resize?: boolean;
}

/**
 * TextArea Component
 *
 * A styled textarea component that integrates with the design system
 * and provides consistent theming across light and dark modes.
 */
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(({
  className,
  rows = 3,
  resize = false,
  disabled = false,
  ...props
}, ref) => {
  const { isDark } = useTheme();

  return (
    <textarea
      ref={ref}
      className={cn(
        'w-full px-4 py-2 rounded-lg border transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-primary-500/50 focus:border-primary-500',
        resize ? 'resize-y' : 'resize-none',
        isDark
          ? 'bg-secondary-800 border-gray-500 text-secondary-100 placeholder:text-secondary-400'
          : 'bg-white border-gray-500 text-secondary-900 placeholder:text-secondary-200',
        disabled && 'opacity-50 cursor-not-allowed',
        className
      )}
      rows={rows}
      disabled={disabled}
      {...props}
    />
  );
});

TextArea.displayName = 'TextArea';

export default TextArea;